import React, { useEffect, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Snackbar,
  Alert,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import httpRequest from "../utils/httpRequest";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { banksList } from "../utils/constant";
import { setFavicon, resetFavicon } from '../utils/setFavicon';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

const Firms = () => {
  const [editingBankId, setEditingBankId] = useState(null);
  const [editingBankData, setEditingBankData] = useState({});
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openFirmDialog, setOpenFirmDialog] = useState(false);
  const [openBankDialog, setOpenBankDialog] = useState(false);
  const [openViewBanksDialog, setOpenViewBanksDialog] = useState(false);
  const [newFirm, setNewFirm] = useState({
    name: "",
    logo: null,
    address: "",
    gst_number: "",
    mobile: "",
    email: "",
    qr_code_url: null,
  });

  const [newBank, setNewBank] = useState({
    firm_id: "",
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    branch: "",
    show_on_invoice: false,
  });

  const [banks, setBanks] = useState([]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [editingFirmId, setEditingFirmId] = useState(null);
  const [editingFirmData, setEditingFirmData] = useState({
    qr_code_url: null,
  });

  const [openImagePreview, setOpenImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [previewImageTitle, setPreviewImageTitle] = useState("");

  const [faviconPref, setFaviconPref] = useState(() => {
    const pref = localStorage.getItem('faviconPreference');
    return pref ? JSON.parse(pref) : { type: 'default' };
  });

  useEffect(() => {
    const handleStorage = () => {
      const pref = localStorage.getItem('faviconPreference');
      setFaviconPref(pref ? JSON.parse(pref) : { type: 'default' });
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const handleEditBank = (bank) => {
    setEditingBankId(bank.id);
    setEditingBankData({ ...bank });
  };

  const handleEditingBankChange = (e) => {
    const { name, value } = e.target;
    setEditingBankData({ ...editingBankData, [name]: value });
  };

  const handleSaveEditedBank = async () => {
    try {
      await httpRequest(
        `firm/editbank/${editingBankId}`,
        "POST",
        {},
        {
          bankName: editingBankData.bank_name,
          accountNumber: editingBankData.account_number,
          ifscCode: editingBankData.ifsc_code,
          branch: editingBankData.branch,
          showOnInvoice: editingBankData.show_on_invoice,
          firmId: editingBankData.firm_id,
        }
      );

      await fetchBanks(editingBankData.firm_id);
      setSnackbar({
        open: true,
        message: "Bank updated successfully!",
        severity: "success",
      });

      setEditingBankId(null);
      setEditingBankData({});
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || "something went wrong",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    const fetchFirms = async () => {
      setLoading(true);
      try {
        const data = await httpRequest("firm/getAll", "GET");
        setFirms(data);
      } catch (err) {
        setError("Failed to fetch firm data");
      } finally {
        setLoading(false);
      }
    };

    fetchFirms();
  }, []);

  const handleOpenFirmDialog = () => {
    setOpenFirmDialog(true);
  };

  const handleCloseFirmDialog = () => {
    setOpenFirmDialog(false);
    setNewFirm({ name: "", logo: null });
  };

  const handleOpenBankDialog = (firmId) => {
    setNewBank({ ...newBank, firm_id: firmId });
    setOpenBankDialog(true);
  };

  const handleCloseBankDialog = () => {
    setOpenBankDialog(false);
    setNewBank({
      firm_id: "",
      bank_name: "",
      account_number: "",
      ifsc_code: "",
      branch: "",
      show_on_invoice: false,
    });
  };

  const handleOpenViewBanksDialog = (firmId) => {
    fetchBanks(firmId);
    setOpenViewBanksDialog(true);
  };

  const handleCloseViewBanksDialog = () => {
    setOpenViewBanksDialog(false);
    setBanks([]);
  };

  const fetchBanks = async (firmId) => {
    try {
      const data = await httpRequest(`firm/banks/${firmId}`, "GET");
      setBanks(data);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to fetch banks for the selected firm",
        severity: "error",
      });
    }
  };

  const handleFirmChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setNewFirm({ ...newFirm, [name]: files[0] });
    } else {
      setNewFirm({ ...newFirm, [name]: value });
    }
  };

  const handleBankChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewBank({
      ...newBank,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmitFirm = async () => {
    try {
      const formData = new FormData();
      formData.append("name", newFirm.name);
      if (newFirm.logo) {
        formData.append("logo", newFirm.logo);
      }
      if (newFirm.qr_code_url) {
        formData.append("qr_code_url", newFirm.qr_code_url);
      }
      formData.append("address", newFirm.address);
      formData.append("gst_number", newFirm.gst_number);
      formData.append("mobile", newFirm.mobile);
      formData.append("email", newFirm.email);

      await httpRequest("firm/add", "POST", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const updatedFirms = await httpRequest("firm/getAll", "GET");
      setFirms(updatedFirms);
      setSnackbar({
        open: true,
        message: "Firm added successfully!",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Failed to add firm",
        severity: "error",
      });
    } finally {
      handleCloseFirmDialog();
    }
  };

  const handleSubmitBank = async () => {
    try {
      const bankData = {
        firm_id: newBank.firm_id,
        bank_name: newBank.bank_name,
        account_number: newBank.account_number,
        ifsc_code: newBank.ifsc_code,
        branch: newBank.branch,
        show_on_invoice: newBank.showOnInvoice,
      };
      await httpRequest("firm/bank/add", "POST", bankData);

      const updatedFirms = await httpRequest("firm/getAll", "GET");
      setFirms(updatedFirms);

      setSnackbar({
        open: true,
        message: "Bank added successfully!",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Failed to add bank",
        severity: "error",
      });
    } finally {
      handleCloseBankDialog();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleToggleActive = async (id, isActive) => {
    try {
      await httpRequest(`firm/update/${id}`, "PATCH", { is_active: !isActive });
      const updatedFirms = await httpRequest("firm/getAll", "GET");
      setFirms(updatedFirms);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to update firm status",
        severity: "error",
      });
    }
  };

  const handleEditFirm = (firm) => {
    setEditingFirmId(firm.id);
    setEditingFirmData({ ...firm });
  };

  const handleEditingFirmChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setEditingFirmData({ ...editingFirmData, [name]: files[0] });
    } else {
      setEditingFirmData({ ...editingFirmData, [name]: value });
    }
  };

  const handleSaveEditedFirm = async () => {
    try {
      const formData = new FormData();
      formData.append("name", editingFirmData.name);
      if (editingFirmData.logo && editingFirmData.logo instanceof File) {
        formData.append("logo", editingFirmData.logo);
      }
      if (editingFirmData.qr_code_url && editingFirmData.qr_code_url instanceof File) {
        formData.append("qr_code_url", editingFirmData.qr_code_url);
      }
      formData.append("address", editingFirmData.address);
      formData.append("gst_number", editingFirmData.gst_number);
      formData.append("mobile", editingFirmData.mobile);
      formData.append("email", editingFirmData.email);

      await httpRequest(`firm/update/${editingFirmId}`, "POST", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const updatedFirms = await httpRequest("firm/getAll", "GET");
      setFirms(updatedFirms);
      setSnackbar({
        open: true,
        message: "Firm updated successfully!",
        severity: "success",
      });

      setEditingFirmId(null);
      setEditingFirmData({});
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || "Failed to update firm",
        severity: "error",
      });
    }
  };

  const handleOpenImagePreview = (url, title) => {
    setPreviewImageUrl(url);
    setPreviewImageTitle(title);
    setOpenImagePreview(true);
  };

  const handleCloseImagePreview = () => {
    setOpenImagePreview(false);
    setPreviewImageUrl(null);
    setPreviewImageTitle("");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", marginTop: 2 }}>
      <Typography variant="h4" gutterBottom>
        Firms Management
      </Typography>

      <Button
        variant="contained"
        color="primary"
        onClick={handleOpenFirmDialog}
        sx={{ marginBottom: 2 }}
      >
        Add Firm
      </Button>

      {loading ? (
        <Typography variant="h6">Loading...</Typography>
      ) : error ? (
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="firm details table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Logo</TableCell>
                <TableCell>QR Code</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>GST Number</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {firms.length ? (
                firms.map((firm) => (
                  <TableRow key={firm.id}>
                    <TableCell>{firm.name}</TableCell>
                    <TableCell>
                      {firm.logo ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, position: "relative" }}>
                            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                              <Tooltip title="Click to view larger image">
                                <span>
                                  <img
                                    src={firm.logo}
                                    alt="Firm Logo"
                                    width="50"
                                    height="50"
                                    onClick={() => handleOpenImagePreview(firm.logo, `${firm.name} - Logo`)}
                                    style={{ cursor: "pointer", borderRadius: "4px" }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenImagePreview(firm.logo, `${firm.name} - Logo`)}
                                    sx={{
                                      padding: 0.5,
                                      opacity: 0,
                                      transition: "opacity 0.2s ease-in-out",
                                      position: "absolute",
                                      right: "-8px",
                                      top: "-8px",
                                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                                      "&:hover": {
                                        backgroundColor: "rgba(255, 255, 255, 1)",
                                      }
                                    }}
                                    className="zoom-icon"
                                  >
                                    <ZoomInIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                            <Tooltip title="Use this firm's logo as favicon">
                              <Switch
                                checked={faviconPref.type === 'firm' && faviconPref.logo === firm.logo}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setFavicon(firm.logo);
                                    localStorage.setItem('faviconPreference', JSON.stringify({ type: 'firm', logo: firm.logo }));
                                    setFaviconPref({ type: 'firm', logo: firm.logo });
                                  } else {
                                    resetFavicon();
                                    localStorage.setItem('faviconPreference', JSON.stringify({ type: 'default' }));
                                    setFaviconPref({ type: 'default' });
                                  }
                                }}
                                color="primary"
                                size="small"
                                sx={{ mt: 1 }}
                              />
                            </Tooltip>
                          </Box>
                        </Box>
                      ) : (
                        "No Logo"
                      )}
                    </TableCell>
                    <TableCell>
                      {firm.qr_code_url ? (
                        <Tooltip title="Click to view larger image">
                          <Box 
                            sx={{ 
                              display: "flex", 
                              alignItems: "center", 
                              gap: 1,
                              position: "relative",
                              "&:hover .zoom-icon": {
                                opacity: 1,
                              }
                            }}
                          >
                            <img
                              src={firm.qr_code_url}
                              alt="QR Code"
                              width="50"
                              height="50"
                              onClick={() => handleOpenImagePreview(firm.qr_code_url, `${firm.name} - QR Code`)}
                              style={{ cursor: "pointer", borderRadius: "4px" }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleOpenImagePreview(firm.qr_code_url, `${firm.name} - QR Code`)}
                              sx={{ 
                                padding: 0.5,
                                opacity: 0,
                                transition: "opacity 0.2s ease-in-out",
                                position: "absolute",
                                right: "-8px",
                                top: "-8px",
                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                                "&:hover": {
                                  backgroundColor: "rgba(255, 255, 255, 1)",
                                }
                              }}
                              className="zoom-icon"
                            >
                              <ZoomInIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Tooltip>
                      ) : (
                        "No QR Code"
                      )}
                    </TableCell>
                    <TableCell>{firm.address || "—"}</TableCell>
                    <TableCell>{firm.gst_number || "—"}</TableCell>
                    <TableCell>{firm.mobile || "—"}</TableCell>
                    <TableCell>{firm.email || "—"}</TableCell>
                    <TableCell>
                      <Switch
                        checked={firm.is_active}
                        onChange={() =>
                          handleToggleActive(firm.id, firm.is_active)
                        }
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: 'center' }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => handleEditFirm(firm)}
                          startIcon={<EditIcon />}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => handleOpenBankDialog(firm.id)}
                          startIcon={<AddCircleIcon />}
                        >
                          Add Bank
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={() => handleOpenViewBanksDialog(firm.id)}
                          startIcon={<VisibilityIcon />}
                        >
                          View Banks
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10}>No firms found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openFirmDialog} onClose={handleCloseFirmDialog}>
        <DialogTitle>Add New Firm</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            name="name"
            value={newFirm.name}
            onChange={handleFirmChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Address"
            name="address"
            value={newFirm.address}
            onChange={handleFirmChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="GST Number"
            name="gst_number"
            value={newFirm.gst_number}
            onChange={handleFirmChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Mobile"
            name="mobile"
            value={newFirm.mobile}
            onChange={handleFirmChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            value={newFirm.email}
            onChange={handleFirmChange}
            fullWidth
            margin="normal"
          />
          <input
            type="file"
            name="logo"
            accept="image/png"
            onChange={handleFirmChange}
            style={{ marginTop: "10px" }}
          />
          <br />
          <label>QR Code Image:</label>
          <input
            type="file"
            name="qr_code_url"
            accept="image/*"
            onChange={handleFirmChange}
            style={{ marginTop: "10px" }}
          />
          {newFirm.qr_code_url && typeof newFirm.qr_code_url !== 'string' && (
            <img
              src={URL.createObjectURL(newFirm.qr_code_url)}
              alt="QR Preview"
              style={{ width: 100, height: 100, marginTop: 8 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFirmDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmitFirm} color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openBankDialog} onClose={handleCloseBankDialog}>
        <DialogTitle>Add Bank to Firm</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Bank Name</InputLabel>
            <Select
              label="Bank Name"
              name="bank_name"
              value={newBank.bank_name}
              onChange={handleBankChange}
            >
              {banksList.map((bank) => (
                <MenuItem key={bank} value={bank}>
                  {bank}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Account Number"
            name="account_number"
            value={newBank.account_number}
            onChange={handleBankChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="IFSC Code"
            name="ifsc_code"
            value={newBank.ifsc_code}
            onChange={handleBankChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Branch"
            name="branch"
            value={newBank.branch}
            onChange={handleBankChange}
            fullWidth
            margin="normal"
          />
          <FormControl margin="normal" component="fieldset">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Switch
                checked={newBank.show_on_invoice}
                onChange={handleBankChange}
                name="show_on_invoice"
                color="primary"
              />
              <Typography>Show on Invoice</Typography>
            </Box>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBankDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmitBank} color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        PaperProps={{ sx: { maxWidth: "80vw" } }}
        open={openViewBanksDialog}
        onClose={handleCloseViewBanksDialog}
      >
        <DialogTitle>Banks for Selected Firm</DialogTitle>
        <DialogContent>
          {banks.length ? (
            <Table aria-label="banks table">
              <TableHead>
                <TableRow>
                  <TableCell>Bank Name</TableCell>
                  <TableCell>Account Number</TableCell>
                  <TableCell>IFSC Code</TableCell>
                  <TableCell>Branch</TableCell>
                  <TableCell>Show on Invoice</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {banks.map((bank) => (
                  <TableRow key={bank.id}>
                    {editingBankId === bank.id ? (
                      <>
                        <TableCell>
                          <Select
                            value={editingBankData.bank_name}
                            name="bank_name"
                            onChange={handleEditingBankChange}
                            fullWidth
                          >
                            {banksList.map((bankName) => (
                              <MenuItem key={bankName} value={bankName}>
                                {bankName}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={editingBankData.account_number}
                            name="account_number"
                            onChange={handleEditingBankChange}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={editingBankData.ifsc_code}
                            name="ifsc_code"
                            onChange={handleEditingBankChange}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={editingBankData.branch}
                            name="branch"
                            onChange={handleEditingBankChange}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  editingBankData.show_on_invoice || false
                                }
                                onChange={(e) =>
                                  setEditingBankData({
                                    ...editingBankData,
                                    show_on_invoice: e.target.checked,
                                  })
                                }
                                name="show_on_invoice"
                                color="primary"
                              />
                            }
                            label="Show"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            onClick={handleSaveEditedBank}
                            color="primary"
                          >
                            Save
                          </Button>
                          <Button
                            onClick={() => {
                              setEditingBankId(null);
                              setEditingBankData({});
                            }}
                            color="primary"
                          >
                            Cancel
                          </Button>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{bank.bank_name}</TableCell>
                        <TableCell>{bank.account_number}</TableCell>
                        <TableCell>{bank.ifsc_code}</TableCell>
                        <TableCell>{bank.branch}</TableCell>
                        <TableCell>
                          {bank.show_on_invoice ? "Yes" : "No"}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => handleEditBank(bank)}
                            startIcon={<EditIcon />}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography>No banks found for this firm</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewBanksDialog} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editingFirmId !== null} onClose={() => setEditingFirmId(null)}>
        <DialogTitle>Edit Firm</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            name="name"
            value={editingFirmData.name || ""}
            onChange={handleEditingFirmChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Address"
            name="address"
            value={editingFirmData.address || ""}
            onChange={handleEditingFirmChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="GST Number"
            name="gst_number"
            value={editingFirmData.gst_number || ""}
            onChange={handleEditingFirmChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Mobile"
            name="mobile"
            value={editingFirmData.mobile || ""}
            onChange={handleEditingFirmChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            value={editingFirmData.email || ""}
            onChange={handleEditingFirmChange}
            fullWidth
            margin="normal"
          />
          <input
            type="file"
            name="logo"
            accept="image/png"
            onChange={handleEditingFirmChange}
            style={{ marginTop: "10px" }}
          />
          <br />
          <label>QR Code Image:</label>
          <input
            type="file"
            name="qr_code_url"
            accept="image/*"
            onChange={handleEditingFirmChange}
            style={{ marginTop: "10px" }}
          />
          {editingFirmData.qr_code_url && typeof editingFirmData.qr_code_url !== 'string' && (
            <img
              src={URL.createObjectURL(editingFirmData.qr_code_url)}
              alt="QR Preview"
              style={{ width: 100, height: 100, marginTop: 8 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingFirmId(null)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSaveEditedFirm} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openImagePreview}
        onClose={handleCloseImagePreview}
        PaperProps={{ sx: { maxWidth: "90vw", maxHeight: "90vh" } }}
      >
        <DialogTitle>{previewImageTitle || "Image Preview"}</DialogTitle>
        <DialogContent sx={{ padding: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
          {previewImageUrl && (
            <img 
              src={previewImageUrl} 
              alt="Preview" 
              style={{ 
                maxWidth: "100%", 
                maxHeight: "calc(90vh - 120px)", 
                objectFit: "contain" 
              }} 
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImagePreview} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Firms;
